import React, { useRef } from 'react';
import styles from './TransportationMapColumnCard.module.scss';
import clsx from 'clsx';
import { ReactComponent as Plane } from 'assets/icons/airplane.svg';
import { ReactComponent as Boat } from 'assets/icons/boat.svg';
import { ReactComponent as Road } from 'assets/icons/truck.svg';
import { TransportationColumnCardsEnums } from 'enums/TransportationColumnCardsEnums';
import EmissionCalculationError from '../EmissionCalculationError';
import { ExpandIconState } from '../RawMaterialMapColumnCard';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import { Tooltip, Typography } from 'antd';
import formatNumberWithCommas from 'shared/utils/formatNumberWithComma';

interface componentPropsInterface {
  style?: React.CSSProperties;
}
export interface TransportationColumnCardProps {
  name?: string;
  weight?: string;
  carbon?: string;
  emission: number;
  originToSupplierLegType?: string;
  supplierToPfLegType?: string;
  unit?: string;
  legType?: string;
  activityLabel: string;
  id?: string;
  isOrigin?: boolean;
  expandIcon?: ExpandIconState;
  isTop?: boolean;
  isBottom?: boolean;
  isDownloadPage?: boolean;
}
const TransportationColumnCard: React.FC<
  TransportationColumnCardProps & componentPropsInterface
> = ({
  name,
  weight,
  carbon,
  emission,
  unit,
  legType,
  activityLabel,
  originToSupplierLegType,
  supplierToPfLegType,
  id,
  isOrigin,
  isTop,
  isBottom,
  expandIcon,
  isDownloadPage,
  ...componentProps
}: TransportationColumnCardProps & componentPropsInterface) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const handleColorByEmission = (value: number) => {
    let category = '';
    if (value >= 0 && value <= 20) {
      category = TransportationColumnCardsEnums.BLUE;
    } else if (value > 20 && value <= 40) {
      category = TransportationColumnCardsEnums.GREEN;
    } else if (value > 40 && value <= 60) {
      category = TransportationColumnCardsEnums.YELLOW;
    } else if (value > 60 && value <= 80) {
      category = TransportationColumnCardsEnums.ORANGE;
    } else if (value > 80) {
      category = TransportationColumnCardsEnums.RED;
    }
    return category;
  };

  const { style } = componentProps;

  const isOriginError =
    originToSupplierLegType === '' || originToSupplierLegType === undefined
      ? true
      : false;
  const isSupplierError =
    supplierToPfLegType === '' || supplierToPfLegType === undefined
      ? true
      : false;

  const isError =
    (isOriginError || isSupplierError) && activityLabel?.trim() !== 'Tap Water';
  const carbonEmissionValue = parseFloat(carbon ?? '').toFixed(4);
  const colorCodingClass = {
    [styles.green]:
      handleColorByEmission(emission) === TransportationColumnCardsEnums.GREEN,
    [styles.blue]:
      handleColorByEmission(emission) === TransportationColumnCardsEnums.BLUE,
    [styles.yellow]:
      handleColorByEmission(emission) === TransportationColumnCardsEnums.YELLOW,
    [styles.orange]:
      handleColorByEmission(emission) === TransportationColumnCardsEnums.ORANGE,
    [styles.red]:
      handleColorByEmission(emission) === TransportationColumnCardsEnums.RED,
  };
  return (
    <div ref={cardRef} style={style} className="canvas-card" id={id}>
      <EmissionCalculationError showError={isError}>
        <div
          className={clsx(
            {
              [styles.cardContainer]: true,
              [styles.originCard]: isOrigin,
              [styles.top]: isTop,
              [styles.hasTooltip]: !isDownloadPage,
              [styles.bottom]: isBottom,
              [styles.error]: isError,
            },
            !isOrigin && colorCodingClass
          )}>
          <div className={styles.contentContainer}>
            {isOrigin && (
              <div
                className={clsx(
                  styles.originColor,
                  !isError && colorCodingClass
                )}></div>
            )}
            <div className={styles.dataContainer}>
              <div className={styles.label}>
                {(isOrigin || !expandIcon) && (
                  <div className={styles.iconContainer}>
                    {legType === TransportationColumnCardsEnums.ROAD ? (
                      <Road />
                    ) : legType ===
                      TransportationColumnCardsEnums.ROAD_SEA_ROAD ? (
                      <div className={styles.legIconsContainer}>
                        <Road /> <Boat />
                        <Road />
                      </div>
                    ) : legType ===
                      TransportationColumnCardsEnums.ROAD_AIR_ROAD ? (
                      <div className={styles.legIconsContainer}>
                        <Road /> <Plane />
                        <Road />
                      </div>
                    ) : null}
                  </div>
                )}
                <Tooltip title={name}>
                  <Typography.Paragraph
                    ellipsis={!isDownloadPage && { rows: 1 }}>
                    {name}
                  </Typography.Paragraph>
                </Tooltip>
              </div>
              <div className={styles.data}>
                <p>
                  {formatNumberWithCommas(carbonEmissionValue)} kgCO<sub>2</sub>
                  e
                </p>
              </div>
            </div>
            {expandIcon && (
              <div
                className={clsx({
                  [styles.expandIcon]: true,
                  [styles.expanded]: expandIcon === ExpandIconState.OPEN,
                })}>
                <Arrow fontSize={44} />
              </div>
            )}
          </div>
        </div>
      </EmissionCalculationError>
    </div>
  );
};

export default TransportationColumnCard;
